import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { ArrowLeft, Download, Upload, AlertCircle, CheckCircle2, FileText } from "lucide-react";
import { useState } from "react";
import { useLocation } from "wouter";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";

export default function CSVImport() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  
  const [csvFile, setCSVFile] = useState<File | null>(null);
  const [csvText, setCSVText] = useState("");
  const [csvHeaders, setCSVHeaders] = useState<string[]>([]);
  const [step, setStep] = useState<"upload" | "mapping" | "preview" | "complete">("upload");
  
  // Column mapping state
  const [mapping, setMapping] = useState<Record<string, string>>({
    wellName: "",
    logDate: "",
  });
  
  const [validationResult, setValidationResult] = useState<any>(null);
  const [importResult, setImportResult] = useState<any>(null);

  const validateMutation = trpc.import.validate.useMutation();
  const executeMutation = trpc.import.execute.useMutation();
  const templateQuery = trpc.import.template.useQuery();

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setCSVFile(file);
    
    const text = await file.text();
    setCSVText(text);
    
    // Parse headers
    const lines = text.trim().split('\n');
    if (lines.length > 0) {
      const headers = lines[0]!.split(',').map(h => h.trim().replace(/^"|"$/g, ''));
      setCSVHeaders(headers);
      
      // Auto-map if headers match expected names
      const autoMapping: Record<string, string> = {};
      const expectedFields = [
        'wellName', 'logDate', 'logTime', 'operatorInitial',
        'meterReadStart', 'meterReadEnd', 'gpm', 'hours', 'remarks',
        'measuredInchesCl2', 'inchesUsedCl2', 'lbsCl2', 'freeResidualPpm', 'psi', 'chemicalNotes'
      ];
      
      expectedFields.forEach(field => {
        const match = headers.find(h => h.toLowerCase() === field.toLowerCase());
        if (match) {
          autoMapping[field] = match;
        }
      });
      
      setMapping(prev => ({ ...prev, ...autoMapping }));
      setStep("mapping");
    }
  };

  const handleValidate = async () => {
    if (!mapping.wellName || !mapping.logDate) {
      toast.error("Please map at least Well Name and Log Date");
      return;
    }

    try {
      const result = await validateMutation.mutateAsync({
        csvText,
        mapping: mapping as any,
      });
      
      setValidationResult(result);
      setStep("preview");
      
      if (result.hasErrors) {
        toast.warning(`Found ${result.errors.length} validation errors`);
      } else {
        toast.success("All rows validated successfully!");
      }
    } catch (error: any) {
      toast.error(error.message || "Validation failed");
    }
  };

  const handleImport = async () => {
    if (!csvFile) return;

    try {
      const result = await executeMutation.mutateAsync({
        csvText,
        mapping: mapping as any,
        fileName: csvFile.name,
      });
      
      setImportResult(result);
      setStep("complete");
      
      if (result.status === 'completed') {
        toast.success(`Successfully imported ${result.successCount} logs`);
      } else if (result.status === 'partial') {
        toast.warning(`Imported ${result.successCount} logs, ${result.failedCount} failed`);
      } else {
        toast.error(`Import failed: ${result.failedCount} rows had errors`);
      }
    } catch (error: any) {
      toast.error(error.message || "Import failed");
    }
  };

  const downloadErrorReport = () => {
    if (!validationResult && !importResult) return;
    
    const errors = validationResult?.errors || importResult?.errors || [];
    const csv = "Row,Field,Error\n" + errors.map((e: any) => 
      `${e.row},"${e.field}","${e.message}"`
    ).join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'import-errors.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadTemplate = () => {
    if (!templateQuery.data) return;
    
    const blob = new Blob([templateQuery.data.csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'well-logs-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (user?.role !== 'admin' && user?.role !== 'supervisor') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card>
          <CardHeader>
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>Supervisor or Admin access required to import CSV data</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="container py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <h1 className="text-xl font-bold">CSV Import</h1>
          </div>
        </div>
      </header>

      <main className="container py-6 max-w-4xl">
        {/* Progress Steps */}
        <div className="flex items-center gap-2 mb-6">
          {["upload", "mapping", "preview", "complete"].map((s, i) => (
            <div key={s} className="flex items-center">
              <Badge variant={step === s ? "default" : "outline"}>
                {i + 1}. {s.charAt(0).toUpperCase() + s.slice(1)}
              </Badge>
              {i < 3 && <div className="w-8 h-0.5 bg-border mx-2" />}
            </div>
          ))}
        </div>

        {/* Step 1: Upload */}
        {step === "upload" && (
          <Card>
            <CardHeader>
              <CardTitle>Upload CSV File</CardTitle>
              <CardDescription>
                Import historical well log data from a CSV file
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="csv-file">Select CSV File</Label>
                <input
                  id="csv-file"
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="block w-full mt-2 text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
                />
              </div>
              
              <div className="flex gap-2">
                <Button variant="outline" onClick={downloadTemplate} disabled={!templateQuery.data}>
                  <Download className="w-4 h-4 mr-2" />
                  Download Template
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Column Mapping */}
        {step === "mapping" && (
          <Card>
            <CardHeader>
              <CardTitle>Map CSV Columns</CardTitle>
              <CardDescription>
                Match your CSV columns to the expected fields. Required fields are marked with *
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="map-wellName">Well Name *</Label>
                  <Select value={mapping.wellName} onValueChange={(v) => setMapping(prev => ({ ...prev, wellName: v }))}>
                    <SelectTrigger id="map-wellName">
                      <SelectValue placeholder="Select column" />
                    </SelectTrigger>
                    <SelectContent>
                      {csvHeaders.map(h => (
                        <SelectItem key={h} value={h}>{h}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="map-logDate">Log Date *</Label>
                  <Select value={mapping.logDate} onValueChange={(v) => setMapping(prev => ({ ...prev, logDate: v }))}>
                    <SelectTrigger id="map-logDate">
                      <SelectValue placeholder="Select column" />
                    </SelectTrigger>
                    <SelectContent>
                      {csvHeaders.map(h => (
                        <SelectItem key={h} value={h}>{h}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Optional fields */}
                {["logTime", "operatorInitial", "meterReadStart", "meterReadEnd", "gpm", "hours", "remarks"].map(field => (
                  <div key={field}>
                    <Label htmlFor={`map-${field}`}>{field}</Label>
                    <Select value={mapping[field] || ""} onValueChange={(v) => setMapping(prev => ({ ...prev, [field]: v }))}>
                      <SelectTrigger id={`map-${field}`}>
                        <SelectValue placeholder="Skip" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">Skip</SelectItem>
                        {csvHeaders.map(h => (
                          <SelectItem key={h} value={h}>{h}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>

              <div className="flex gap-2">
                <Button onClick={() => setStep("upload")} variant="outline">
                  Back
                </Button>
                <Button onClick={handleValidate} disabled={validateMutation.isPending}>
                  {validateMutation.isPending ? "Validating..." : "Validate & Preview"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Preview & Validation */}
        {step === "preview" && validationResult && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Validation Results</CardTitle>
                <CardDescription>
                  {validationResult.totalRows} rows found
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {validationResult.hasErrors ? (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Validation Errors</AlertTitle>
                    <AlertDescription>
                      {validationResult.errors.length} errors found. You can proceed with partial import or fix errors and re-upload.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert>
                    <CheckCircle2 className="h-4 w-4" />
                    <AlertTitle>All Clear!</AlertTitle>
                    <AlertDescription>
                      All rows passed validation. Ready to import.
                    </AlertDescription>
                  </Alert>
                )}

                {validationResult.errors.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Errors ({validationResult.errors.length})</Label>
                      <Button variant="outline" size="sm" onClick={downloadErrorReport}>
                        <Download className="w-4 h-4 mr-2" />
                        Download Error Report
                      </Button>
                    </div>
                    <div className="max-h-60 overflow-y-auto border rounded-md p-3 space-y-2">
                      {validationResult.errors.slice(0, 20).map((err: any, i: number) => (
                        <div key={i} className="text-sm">
                          <span className="font-medium">Row {err.row}:</span> {err.field} - {err.message}
                        </div>
                      ))}
                      {validationResult.errors.length > 20 && (
                        <p className="text-sm text-muted-foreground">
                          ... and {validationResult.errors.length - 20} more errors
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button onClick={() => setStep("mapping")} variant="outline">
                    Back to Mapping
                  </Button>
                  <Button onClick={handleImport} disabled={executeMutation.isPending}>
                    {executeMutation.isPending ? "Importing..." : "Import"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 4: Complete */}
        {step === "complete" && importResult && (
          <Card>
            <CardHeader>
              <CardTitle>Import Complete</CardTitle>
              <CardDescription>
                {importResult.status === 'completed' && "All rows imported successfully"}
                {importResult.status === 'partial' && "Import completed with some errors"}
                {importResult.status === 'failed' && "Import failed"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold">{importResult.totalRows}</div>
                  <div className="text-sm text-muted-foreground">Total Rows</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{importResult.successCount}</div>
                  <div className="text-sm text-muted-foreground">Successful</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">{importResult.failedCount}</div>
                  <div className="text-sm text-muted-foreground">Failed</div>
                </div>
              </div>

              {importResult.errors.length > 0 && (
                <Button variant="outline" onClick={downloadErrorReport}>
                  <Download className="w-4 h-4 mr-2" />
                  Download Error Report
                </Button>
              )}

              <div className="flex gap-2">
                <Button onClick={() => {
                  setStep("upload");
                  setCSVFile(null);
                  setCSVText("");
                  setValidationResult(null);
                  setImportResult(null);
                }}>
                  Import Another File
                </Button>
                <Button variant="outline" onClick={() => navigate("/import-history")}>
                  <FileText className="w-4 h-4 mr-2" />
                  View Import History
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
